// PaymentForm.jsx
// Updated: 06-04-2026
// Changes:
// - Deal is now pre-selected from the PendingDealCard click — no deal dropdown shown
//   when creating a new payment (the deal context is passed via `deal` prop)
// - All deal info (name, value, close date) is shown as a read-only banner
// - dueDate auto-filled from deal.expectedCloseDate, editable by user
// - Payment type: Installment, Commission, Other only
// - step="any" on amount input for decimal support
// - When editing an existing payment, normal edit mode is used (deal shown read-only)

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Save, Info, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import "../../css/payments/paymentform.css";

const toDateInput = (val) => {
  if (!val) return "";
  try { return new Date(val).toISOString().slice(0, 10); }
  catch { return ""; }
};

export default function PaymentForm({
  payment,       // existing payment — passed when editing
  deal,          // pre-selected deal — passed from PendingDealCard click
  deals = [],    // full deals list — still used in edit mode to resolve dealId
  onSubmit,
  onCancel,
  isLoading,
}) {
  /* Resolve the deal object:
     - creating: `deal` prop is the full deal object from PendingDealCard
     - editing:  resolve from `deals` array using payment.dealId             */
  const resolvedDeal = deal || (
    payment
      ? deals.find((d) => {
          const pid = payment.dealId?._id || payment.dealId;
          return d._id === pid || d._id?.toString() === pid?.toString();
        }) || payment.dealId   // if populated object
      : null
  );

  const [formData, setFormData] = useState({
    dealId:         resolvedDeal?._id || resolvedDeal?.toString() || "",
    paymentType:    payment?.paymentType    || "Installment",
    amount:         payment?.amount         || resolvedDeal?.dealValue || "",
    paymentDate:    toDateInput(payment?.paymentDate || new Date()),
    dueDate:        toDateInput(payment?.dueDate || resolvedDeal?.expectedCloseDate),
    paymentMethod:  payment?.paymentMethod  || "Bank Transfer",
    status:         payment?.status         || "Pending",
    transactionRef: payment?.transactionRef || "",
    notes:          payment?.notes          || "",
  });

  /* Sync when deal / payment props change (e.g. switching selected card) */
  useEffect(() => {
    const d = deal || resolvedDeal;
    if (!payment && d) {
      setFormData((prev) => ({
        ...prev,
        dealId:  d._id || "",
        amount:  prev.amount || d.dealValue || "",
        dueDate: toDateInput(d.expectedCloseDate),
      }));
    }
  }, [deal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...formData, amount: Number(formData.amount) });
  };

  const displayDeal = resolvedDeal && typeof resolvedDeal === "object" ? resolvedDeal : null;

  return (
    <motion.div
      className="payment-form-wrapper"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="payment-form-card">

        {/* Header */}
        <div className="payment-form-header">
          <h2>{payment ? "Edit Payment" : "Record Payment"}</h2>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="payment-form-body">

            {/* ── Deal info banner (read-only) ── */}
            {displayDeal && (
              <div className="deal-info-banner">
                <Info size={14} />
                <div className="deal-info-text">
                  <span className="deal-info-name">
                    <strong>{displayDeal.dealName || "Unnamed Deal"}</strong>
                  </span>
                  <span>
                    <strong>Deal Value:</strong>{" "}
                    {displayDeal.dealValue
                      ? `₹ ${Number(displayDeal.dealValue).toLocaleString("en-IN")}`
                      : "—"}
                  </span>
                  {displayDeal.expectedCloseDate && (
                    <span>
                      <strong>Expected Close:</strong>{" "}
                      {format(new Date(displayDeal.expectedCloseDate), "dd MMM yyyy")}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Incomplete data warning if deal has missing fields */}
            {displayDeal?.isIncomplete && displayDeal.missingFields?.length > 0 && (
              <div className="incomplete-warning">
                <AlertTriangle size={14} />
                <span>
                  Some deal data is missing ({displayDeal.missingFields.join(", ")}).
                  The payment can still be recorded.
                </span>
              </div>
            )}

            {/* Payment Type & Amount */}
            <div className="form-grid">
              <div className="form-group">
                <label>Payment Type *</label>
                <select name="paymentType" value={formData.paymentType} onChange={handleChange}>
                  <option value="Installment">Installment</option>
                  <option value="Commission">Commission</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount (₹) *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={
                    displayDeal?.dealValue
                      ? `Deal value: ₹${Number(displayDeal.dealValue).toLocaleString("en-IN")}`
                      : "Enter amount"
                  }
                  min="0"
                  step="any"
                  required
                />
              </div>
            </div>

            {/* Payment Date & Due Date */}
            <div className="form-grid">
              <div className="form-group">
                <label>Payment Date *</label>
                <input
                  type="date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                />
                {displayDeal?.expectedCloseDate && (
                  <span className="field-hint">Auto-filled from deal's expected close date</span>
                )}
              </div>
            </div>

            {/* Payment Method & Status */}
            <div className="form-grid">
              <div className="form-group">
                <label>Payment Method *</label>
                <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option value="Pending">Pending</option>
                  <option value="Received">Received</option>
                  <option value="Overdue">Overdue</option>
                </select>
              </div>
            </div>

            {/* Transaction Reference */}
            <div className="form-group">
              <label>Transaction Reference</label>
              <input
                type="text"
                name="transactionRef"
                value={formData.transactionRef}
                onChange={handleChange}
                placeholder="TXN123456"
              />
            </div>

            {/* Notes */}
            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Additional payment notes..."
              />
            </div>

          </div>

          {/* Footer */}
          <div className="payment-form-footer">
            <button type="button" className="btn outline" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={isLoading}>
              <Save size={16} />
              {isLoading ? "Saving..." : "Save Payment"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}